import tablesApi from '../../../../api/tables.js'
import { hashString } from '../../../../composables/String.js'
const emptyColumn = {
    name: '',
    type: '',
    nullable: false,
    default: '',
    hasDefault: false,
    unsigned: false
}
const initialState = () => ({
    databases: {},
    databaseSelection: null,
    tables: {},
    tableSelection: null,
    newTable: {
        name: '',
        columns: [{ ...emptyColumn }]
    }
})

const meta = {
    persistence: {
        databases: {
            storage: 'localStorage'
            // clearOnLogout: true (cleared by default)
        },
        newTable: {
            storage: 'localStorage'
        }
    }
}

const state = initialState

const getters = {
    database: (state) => {
        if (Object.prototype.hasOwnProperty.call(state.databases, state.databaseSelection)) {
            return state.databases[state.databaseSelection]
        }
        return null
    },
    tables: (state) => (databaseId) => {
        if (Object.prototype.hasOwnProperty.call(state.tables, databaseId)) {
            return state.tables[databaseId]
        }
        return []
    },
    selectedTable: (state) => {
        if (Object.keys(state.tables).includes(state.databaseSelection)) {
            return state.tables[state.databaseSelection].find(table => {
                return table.name === state.tableSelection
            })
        }
        return null
    }
}
const mutations = {
    setDatabases (state, payload) {
        payload.forEach(database => {
            state.databases[database.id] = database
        })
    },
    setTables (state, payload) {
        state.tables[payload.databaseId] = payload.tables
    },
    clearState (state) {
        const cleanState = initialState()
        Object.keys(cleanState).forEach(key => {
            state[key] = cleanState[key]
        })
    },
    updateTableSelection (state, tableName) {
        state.tableSelection = tableName
    },
    updateDatabaseSelection (state, databaseId) {
        state.databaseSelection = databaseId
    },
    setTableSchema (state, { databaseId, tableName, schema }) {
        Object.keys(state.tables[databaseId]).forEach(key => {
            if (state.tables[databaseId][key].name === tableName) {
                state.tables[databaseId][key].schema = schema
            }
        })
    },
    setTableData (state, { databaseId, tableName, payload }) {
        Object.keys(state.tables[databaseId]).forEach(key => {
            if (state.tables[databaseId][key].name === tableName) {
                state.tables[databaseId][key].payload = payload
            }
        })
    },
    removeNewTableColumn (state, columnKey) {
        state.newTable.columns.splice(columnKey, 1)
    },
    addNewTableColumn (state) {
        state.newTable.columns.push({ ...emptyColumn })
    }
}
const actions = {
    async createDatabase ({ commit }, { teamId, databaseName = '' }) {
        return tablesApi.createDatabase(teamId, databaseName)
            .then((database) => {
                commit('setDatabases', [database])
                return database
            })
    },
    async getDatabases ({ commit, rootState }) {
        const team = rootState.account?.team
        return tablesApi.getDataBases(team.id)
            .then((databases) => {
                commit('setDatabases', databases)

                return databases
            })
    },
    getTables ({ commit, dispatch, rootState }, databaseId) {
        const team = rootState.account?.team
        return tablesApi.getTables(team.id, databaseId)
            .then((tables) => {
                tables = [...tables].sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
                )

                commit('setTables', { databaseId, tables })

                if (tables.length > 0) {
                    dispatch('updateTableSelection', tables[0].name)
                }
                return tables
            })
    },
    clearState ({ commit }) {
        commit('clearState')
    },
    updateTableSelection ({ commit }, tableName) {
        commit('updateTableSelection', tableName)
    },
    updateDatabaseSelection ({ commit }, databaseId) {
        commit('updateDatabaseSelection', databaseId)
    },
    getTableSchema ({ commit }, { databaseId, tableName, teamId }) {
        return tablesApi.getTableSchema(teamId, databaseId, tableName)
            .then((schema) => {
                schema.forEach(column => {
                    column.safeName = hashString(column.name)
                })
                commit('setTableSchema', { databaseId, tableName, schema })
            })
    },
    getTableData ({ commit }, { databaseId, tableName, teamId }) {
        return tablesApi.getTableData(teamId, databaseId, tableName)
            .then((data) => {
                const payload = {
                    data,
                    safe: data.map(row => Object.fromEntries(
                        Object.entries(row).map(([key, value]) => [hashString(key), value])
                    ))
                }
                commit('setTableData', { databaseId, tableName, payload })
            })
    },
    createTable ({ commit, state, rootState }, { databaseId }) {
        const team = rootState.account?.team
        const sanitizedColumns = state.newTable.columns.map(col => {
            if (!col.hasDefault) delete col.default
            if (!col.unsigned) delete col.unsigned
            return col
        })
        return tablesApi.createTable(team.id, databaseId, { name: state.newTable.name, columns: sanitizedColumns })
    },
    deleteTable ({ commit }, { teamId, databaseId, tableName }) {
        return tablesApi.deleteTable(teamId, databaseId, tableName)
    },
    removeNewTableColumn ({ commit }, columnKey) {
        commit('removeNewTableColumn', columnKey)
    },
    addNewTableColumn ({ commit }) {
        commit('addNewTableColumn')
    }
}

export default {
    namespaced: true,
    state,
    initialState: initialState(),
    getters,
    mutations,
    actions,
    meta
}
